import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader, SectionCard, StatusPill, EmptyState } from "../components/Common";
import { Users, UserPlus, Search } from "lucide-react";

export default function Patients() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canRegister = ["receptionist", "hospital_admin", "super_admin"].includes(user?.role);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async (query = "") => {
    setLoading(true);
    try {
      const { data } = await api.get(`/patients${query ? `?q=${encodeURIComponent(query)}` : ""}`);
      setItems(data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => load(q), 250); return () => clearTimeout(t); }, [q]);

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        title="Patients"
        subtitle="Master patient index. Search by Patient ID, name, phone, National ID, insurance."
        actions={
          canRegister ? (
            <button data-testid="patients-new" onClick={() => navigate("/app/register")} className="px-4 h-10 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white text-sm font-semibold flex items-center gap-1.5"><UserPlus className="w-4 h-4" /> Register patient</button>
          ) : null
        }
      />

      <SectionCard>
        <div className="p-4 border-b border-slate-200 flex gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="patients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search patients…" className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1976D2]/40 focus:border-[#1976D2]" />
          </div>
          <span className="text-xs text-slate-500 self-center">{items.length} results</span>
        </div>
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <EmptyState icon={Users} title="No patients found" description="Try a different search or register a new patient." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2.5">Patient</th>
                  <th className="text-left px-4 py-2.5">Patient ID</th>
                  <th className="text-left px-4 py-2.5">Phone</th>
                  <th className="text-left px-4 py-2.5">District</th>
                  <th className="text-left px-4 py-2.5">Insurance</th>
                  <th className="text-left px-4 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((p) => (
                  <tr key={p.patient_id} data-testid={`patient-row-${p.patient_id}`} className="hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/app/patients/${p.patient_id}`)}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center text-xs font-semibold">
                          {(p.first_name?.[0] || "")}{(p.last_name?.[0] || "")}
                        </div>
                        <div>
                          <div className="font-medium text-slate-800">{p.first_name} {p.last_name}</div>
                          <div className="text-xs text-slate-500">{p.gender} · DOB {p.date_of_birth || "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 tabular text-slate-700">{p.ths_id}</td>
                    <td className="px-4 py-3 tabular text-slate-700">{p.phone_primary}</td>
                    <td className="px-4 py-3">{p.district || "—"}</td>
                    <td className="px-4 py-3">{p.insurance_provider || "None"}</td>
                    <td className="px-4 py-3"><StatusPill status={p.status || "active"} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
